import { ref, watch, unref } from 'vue'

// Live Uber Direct quote for the current delivery address. Fetches a fee/ETA
// from POST /v2/uber/quote, debounced, only when the site offers Uber and the
// order type is delivery. Fail-soft: any error leaves status in a non-blocking
// state — the order is never held up by a missing quote (the server re-quotes
// and decides at checkout).
//
// status: 'idle' | 'loading' | 'ok' | 'no_coverage' | 'unavailable' | 'incomplete'

const DEBOUNCE_MS = 500

function buildUrl(root) {
  // restRoot is the bare WP REST root (…/wp-json/); the v2 namespace lives
  // outside the v1-scoped restUrl, so compose from the root.
  const base = (root || '').replace(/\/$/, '')
  return `${base}/ez-clover/v2/uber/quote`
}

/**
 * @param {Ref<string>} orderType  reactive order type
 * @param {Ref<object>} address    reactive canonical address (street/city/state/zip)
 * @param {object} [root]          _EZCL; defaults to window._EZCL
 */
export function useUberQuote(orderType, address, root) {
  const cfg = root || window?._EZCL || {}
  const siteEnabled = !!cfg?.data?.delivery?.uber?.enabled

  // The site-level flag only says Uber was configured at some point. The live
  // quote must fire ONLY when the selected order type actually routes through
  // the uber_direct provider — otherwise a static (merchant) delivery on an
  // Uber-enabled site would still get a phantom "$7.99 · ~49 min" quote. The
  // provider lives in the order type's nested `delivery` object (per
  // frontend.php), the same source isUberDelivery/deliveryCost read.
  function isUberFor(type) {
    if (!siteEnabled || type !== 'delivery') return false
    return cfg?.data?.services?.[type]?.delivery?.provider === 'uber_direct'
  }

  const status = ref('idle')
  const feeCents = ref(null)
  const etaMinutes = ref(null)
  const quoteId = ref(null)

  let timer = null
  let seq = 0 // guards against out-of-order responses

  function reset() {
    status.value = 'idle'
    feeCents.value = null
    etaMinutes.value = null
    quoteId.value = null
  }

  function complete(addr) {
    return addr && addr.street && addr.city && addr.zip
  }

  async function fetchQuote(addr) {
    const mine = ++seq
    status.value = 'loading'
    try {
      const res = await fetch(buildUrl(cfg.restRoot), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': cfg.nonce || '',
        },
        body: JSON.stringify({
          street: addr.street,
          city: addr.city,
          state: addr.state || '',
          zip: addr.zip,
          country: addr.country || 'US',
        }),
      })
      const data = await res.json()
      if (mine !== seq) return // a newer request superseded this one

      if (data && data.ok) {
        status.value = 'ok'
        feeCents.value = data.feeCents ?? null
        etaMinutes.value = data.etaMinutes ?? null
        quoteId.value = data.quoteId ?? null
      } else {
        status.value = data?.reason === 'no_coverage' ? 'no_coverage' : 'unavailable'
        feeCents.value = null
        etaMinutes.value = null
        quoteId.value = null
      }
    } catch {
      if (mine !== seq) return
      status.value = 'unavailable'
    }
  }

  function schedule(addr) {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => fetchQuote(addr), DEBOUNCE_MS)
  }

  watch(
    [() => unref(orderType), () => unref(address)],
    ([type, addr]) => {
      if (!isUberFor(type)) {
        reset()
        return
      }
      if (!complete(addr)) {
        status.value = 'incomplete'
        return
      }
      schedule(addr)
    },
    { immediate: true, deep: true }
  )

  // Manual retry for the 'unavailable' path — re-quotes the current address
  // immediately (no debounce) when the customer taps "Try again". No-op when
  // Uber is off, the order isn't delivery, or the address is incomplete.
  function refetch() {
    if (!isUberFor(unref(orderType))) return
    const addr = unref(address)
    if (!complete(addr)) return
    if (timer) clearTimeout(timer)
    fetchQuote(addr)
  }

  return { status, feeCents, etaMinutes, quoteId, refetch }
}
